import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  canNavigateToReactAppRoute,
  getMountedReactAppName,
  navigateToReactAppRoute,
  ReactAppNavigationCancelledError,
  ReactAppNavigationRedirectedError,
} from '@github-ui/react-app-navigation'
import type {RouteContext} from '@github-ui/react-app-navigation/route-context'
import {GLOBAL_NAV_CONTEXT_REFRESH_NAVIGATION_STATE} from '@github-ui/react-core/future/apply-router-navigate-override'
import {softNavigate} from '@github-ui/soft-navigate'
import {sendCustomMetric} from '@github-ui/stats'

import {
  getCurrentLocationPath,
  getCurrentSelectedLink,
  getGlobalNavContextRefreshDescriptor,
  getNavigationError,
  GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE,
  locationMatchesGlobalNavContext,
  reloadIfAtLocation,
  setCurrentGlobalNavContext,
} from './context-region-navigation-state'
import {type GlobalNavigationContext, refreshGlobalNavigation} from './refresh-global-navigation'

export interface NavigateWithContextRefreshOptions {
  /** The context to show at the destination. Its `path` is the navigation destination. */
  destinationContext: GlobalNavigationContext
  /** The context currently displayed, restored by browser Back/Forward. */
  sourceContext: GlobalNavigationContext
  /** Additional router navigation state. */
  state?: Record<string, unknown>
}

type ContextRefreshNavigationOutcome = 'accepted' | 'applied' | 'stale' | 'failed' | 'cancelled' | 'redirected'

/**
 * Navigates the mounted React app to a destination with a different global navigation context, and refreshes the
 * context region to match. Both history entries store their context so Back/Forward can restore it.
 *
 * Returns `false` without navigating when `global_nav_context_refresh` is disabled or the app can't handle the
 * destination, so the caller can fall back to a full navigation. After it returns `true`, a failed context refresh falls back to a full navigation of the
 * destination on its own.
 */
export function navigateWithContextRefresh({
  destinationContext,
  sourceContext,
  state,
}: NavigateWithContextRefreshOptions): boolean {
  const appName = getMountedReactAppName()
  if (!isFeatureEnabled('global_nav_context_refresh') || !appName) return false

  const destinationPathname = getPathname(destinationContext.path)
  if (!destinationPathname) return false
  // Check before writing history state, because another router, such as UI Service's TanStack Router, can treat any
  // history write as a navigation and reload the current route.
  if (!canNavigateToReactAppRoute(destinationContext.path, appName)) return false

  const previousHistoryContext = getGlobalNavContextRefreshDescriptor()
  const requestUrl = window.location.href

  // Store the source context before navigating, because React Router can push the destination entry synchronously.
  setCurrentGlobalNavContext(sourceContext)
  const navigationCompletion = navigateToReactAppRoute(destinationContext.path, appName, {
    state: {
      ...state,
      // Repository routes reload navigations to another repository unless they're marked for this destination.
      [GLOBAL_NAV_CONTEXT_REFRESH_NAVIGATION_STATE]: destinationPathname,
      [GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE]: destinationContext,
    },
  })
  if (!navigationCompletion) {
    // Turbo restores this entry on Back, so it doesn't need a descriptor.
    setCurrentGlobalNavContext(previousHistoryContext)
    return false
  }

  const recordOutcome = (outcome: ContextRefreshNavigationOutcome) =>
    sendCustomMetric(
      {
        name: 'GLOBAL_NAVIGATION_CONTEXT_REFRESH_NAVIGATION',
        value: 1,
        tags: {
          mountedApp: appName,
          fromContextType: metricContextType(sourceContext),
          toContextType: metricContextType(destinationContext),
          outcome,
        },
        requestUrl,
      },
      false,
      1,
    )
  recordOutcome('accepted')
  void refreshContextOrNavigate(destinationContext, navigationCompletion, sourceContext, recordOutcome)
  return true
}

/**
 * Resolves the context displayed for the current history entry: one stored by an earlier context navigation, or
 * `context` at the current location. The selected tab is `selectedLink`, falling back to the page's `selected-link`
 * meta tag, and is omitted when neither is set.
 */
export function getDisplayedContext(context: RouteContext, selectedLink?: string): GlobalNavigationContext {
  const historyContext = getGlobalNavContextRefreshDescriptor()
  if (historyContext && locationMatchesGlobalNavContext(historyContext.path)) return historyContext

  const path = getCurrentLocationPath()
  const currentSelectedLink = getCurrentSelectedLink(selectedLink)
  return currentSelectedLink ? {...context, path, selectedLink: currentSelectedLink} : {...context, path}
}

function getPathname(path: string): string | undefined {
  try {
    return new URL(path, window.location.origin).pathname
  } catch {
    return undefined
  }
}

function metricContextType({crumbType}: GlobalNavigationContext): string {
  return ['repository', 'organization', 'team', 'user', 'business', 'preset'].includes(crumbType)
    ? crumbType
    : 'unknown'
}

async function refreshContextOrNavigate(
  destinationContext: GlobalNavigationContext,
  navigationCompletion: Promise<void>,
  sourceContext: GlobalNavigationContext,
  recordOutcome: (outcome: ContextRefreshNavigationOutcome) => void,
) {
  const destination = destinationContext.path
  const [result, navigationError] = await Promise.all([
    refreshGlobalNavigation({...destinationContext, navigationCompletion, currentContext: sourceContext}),
    getNavigationError(navigationCompletion),
  ])
  const outcome =
    navigationError instanceof ReactAppNavigationCancelledError
      ? 'cancelled'
      : navigationError instanceof ReactAppNavigationRedirectedError
        ? 'redirected'
        : navigationError !== undefined
          ? 'failed'
          : result
  recordOutcome(outcome)
  if (result !== 'failed' || navigationError instanceof ReactAppNavigationCancelledError) return

  // A redirect commits a route whose context was not requested, so reload it rather than keep the source context.
  if (navigationError instanceof ReactAppNavigationRedirectedError) {
    reloadIfAtLocation(navigationError.location)
  } else if (!reloadIfAtLocation(destination) && locationMatchesGlobalNavContext(sourceContext.path)) {
    softNavigate(destination)
  }
}
